import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { careersService, JobPositionDetail, CONTRACT_LABELS } from '../services/careersService';
import { JobPositionView } from '../components/careers/JobPositionView';

const EMPLOYMENT_TYPE_SCHEMA: Record<string, string> = {
  tempo_indeterminato: 'FULL_TIME',
  tempo_determinato: 'TEMPORARY',
  apprendistato: 'FULL_TIME',
  stage_tirocinio: 'INTERN',
  partita_iva: 'CONTRACTOR',
  somministrazione: 'TEMPORARY',
};

/** Structured data JobPosting (schema.org) — emesso solo per le posizioni
 * aperte; le chiuse restano visibili ma non indicizzabili. */
function useJobPostingJsonLd(pos: JobPositionDetail | null) {
  useEffect(() => {
    const robotsExisting = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    let robots: HTMLMetaElement | null = null;
    let script: HTMLScriptElement | null = null;

    if (pos && pos.stato === 'chiusa') {
      robots = robotsExisting || document.createElement('meta');
      robots.setAttribute('name', 'robots');
      robots.setAttribute('content', 'noindex');
      if (!robotsExisting) document.head.appendChild(robots);
    }

    if (pos && pos.stato === 'aperta') {
      const payload: any = {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: pos.titolo,
        description: pos.descrizione,
        datePosted: pos.dataPubblicazione || undefined,
        validThrough: pos.dataScadenzaCandidature || undefined,
        employmentType: EMPLOYMENT_TYPE_SCHEMA[pos.tipoContratto] || 'FULL_TIME',
        identifier: pos.riferimento ? { '@type': 'PropertyValue', name: 'DTK S.r.l.', value: pos.riferimento } : undefined,
        hiringOrganization: {
          '@type': 'Organization',
          name: 'DTK S.r.l.',
          sameAs: window.location.origin,
          logo: `${window.location.origin}/logo.png`,
        },
        jobLocation: {
          '@type': 'Place',
          address: { '@type': 'PostalAddress', addressLocality: pos.sede, addressCountry: 'IT' },
        },
        totalJobOpenings: pos.posizioniDisponibili,
        directApply: false,
      };
      if (pos.sedeRemoto === 'remoto') payload.jobLocationType = 'TELECOMMUTE';
      if (pos.retribuzione) {
        payload.baseSalary = {
          '@type': 'MonetaryAmount',
          currency: pos.retribuzione.valuta || 'EUR',
          value: {
            '@type': 'QuantitativeValue',
            minValue: pos.retribuzione.min,
            maxValue: pos.retribuzione.max || pos.retribuzione.min,
            unitText: pos.retribuzione.periodo === 'anno' ? 'YEAR' : pos.retribuzione.periodo === 'mese' ? 'MONTH' : 'HOUR',
          },
        };
      }
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.jobposting = 'true';
      script.textContent = JSON.stringify(payload);
      document.head.appendChild(script);
    }

    return () => {
      if (script) script.remove();
      if (robots && !robotsExisting) robots.remove();
    };
  }, [pos]);
}

export default function CareerDetailPage() {
  const { slug } = useParams();
  const [pos, setPos] = useState<JobPositionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    careersService.getPublic(slug!)
      .then(d => { if (alive) { setPos(d); setNotFound(false); } })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const plainDescr = (pos?.descrizione || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  useSeo(
    pos ? `${pos.titolo} | Lavora con noi DTK` : 'Lavora con noi | DTK',
    pos ? `${CONTRACT_LABELS[pos.tipoContratto]} · ${pos.sede}. ${plainDescr}`.slice(0, 158) : undefined,
  );
  useJobPostingJsonLd(pos);

  return (
    <div data-testid="career-detail-page">
      <div className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span>Azienda</span>
          <ChevronRight className="w-3 h-3" />
          <Link to="/azienda/lavora-con-noi" className="hover:text-foreground transition-colors">Lavora con Noi</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground font-medium">{pos?.titolo || 'Posizione'}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/azienda/lavora-con-noi" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="career-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Torna alle posizioni aperte
        </Link>

        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="career-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento posizione...
          </div>
        )}

        {!loading && notFound && (
          <div className="rounded-xl border bg-white p-8 text-center" data-testid="career-not-found">
            <h1 className="text-2xl font-semibold mb-2">Posizione non trovata</h1>
            <p className="text-sm text-muted-foreground mb-4">La posizione che cerchi non esiste o non è più pubblicata.</p>
            <Link to="/azienda/lavora-con-noi" className="text-sm text-[hsl(var(--dtk-focus))] hover:underline font-medium">
              Vedi le posizioni aperte
            </Link>
          </div>
        )}

        {!loading && pos && (
          <div className="rounded-xl border bg-white p-6 sm:p-8">
            {pos.stato === 'chiusa' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6 flex items-start gap-3" data-testid="career-closed-banner">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold text-amber-900">Questa posizione non è più disponibile</div>
                  <p className="text-amber-800 mt-1">
                    {pos.dataChiusura
                      ? `Le candidature sono state chiuse il ${new Date(pos.dataChiusura).toLocaleDateString('it-IT')}.`
                      : 'Le candidature per questa posizione sono chiuse.'}
                  </p>
                  <Link to="/azienda/lavora-con-noi" className="inline-block mt-2 font-medium text-[hsl(214,58%,41%)] hover:underline" data-testid="career-closed-cta">
                    Vedi le posizioni aperte →
                  </Link>
                </div>
              </div>
            )}

            <JobPositionView
              pos={pos}
              showCta={pos.stato === 'aperta'}
              onApply={() => careersService.trackClick(pos.slug)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
